import {
  WebflowElementText,
  WebflowPlaybookId,
  WebflowPlaybookServiceRequestParams,
} from '@intellimize/dash-shared';
import fetch from '@packages/utilities/fetch';
import {ANALYTICS_EVENTS} from '@packages/systems/core/constants/analytics';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {stripPrefix} from '@packages/systems/designer/users';
import {
  getIsValidSuggestionSelection,
  selectCurrentPlaybookId,
  selectPlaybookCustomPrompt,
} from '@packages/systems/optimization/store';
import {OptimizationVariation} from '@packages/systems/optimization/types';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  selectComponents,
  selectCurrentPage,
  selectFocusedSymbolNativeIdPath,
  selectSelectedElement,
  selectSelectedNativeIdPath,
} from '@packages/utilities/platform-selectors';
import {useQuery} from '@tanstack/react-query';
import {take} from 'lodash';
import {useCallback, useEffect, useState} from 'react';
import {createSelector} from 'reselect';
import {
  UseDesignerSelector,
  UseDispatch,
  UseTrackEvent,
} from '../_internal-shared-types';
import {usePreviousElement} from '../hooks/usePreviousElement';
import {useNotification} from '../mutations/useNotification';
import {
  getAPIProxyPath,
  selectCustomerId,
  selectSiteShortName,
} from '../shared';
import {getPageText} from '../utils/getPageText';
import {getTextFromNativeIdPath} from '../utils/getTextFromNativeIdPath';
import {selectIsSelectedElementInAnotherExperience} from '../utils/selectIsSelectedElementInAnotherExperience';
import {queryKeys} from './query-keys';
import {getTag} from '@packages/utilities/platform';
import {TEMPORARY_EXPERIENCE_ID} from '@packages/systems/optimization/constants';

export const selectIsValidSuggestionSelection = createSelector(
  selectCurrentPlaybookId,
  selectSelectedElement,
  selectCurrentPage,
  selectFocusedSymbolNativeIdPath,
  selectDesignerAbility('canAccessAiSuggestionsComponentMain'),
  getIsValidSuggestionSelection
);

export type SuggestionOptionType = {
  suggestion: string;
  isSelected: boolean;
  override: string | null;
  variationId: string | null;
  variationName: string | null;
  id: string;
};

export type SuggestionOptionsType = SuggestionOptionType[];

export const MAX_SUGGESTIONS = 5;

export const selectCurrentUserEmail = createSelector(
  (state: DesignerStores) => state.UserStore.siteDesigners,
  (state) => state.SiteDataStore.userId,
  (users, userId) => {
    const user = users.get(stripPrefix(userId));
    if (!user) {
      return null;
    }

    return user.email;
  }
);

const selectPageText = createSelector(selectComponents, getPageText);
const selectCustomPrompt = createSelector(
  (state) => state.OptimizationStore,
  selectPlaybookCustomPrompt
);

const getRequestBody = ({
  playbookId,
  customerId,
  userEmail,
  pageText,
  selectedText,
  selectedTag,
  customPrompt,
}: {
  playbookId: WebflowPlaybookId | null;
  customerId: string | null;
  userEmail: string | null;
  pageText: WebflowElementText[] | null;
  selectedText: string | null;
  selectedTag: string | null;
  customPrompt: string | null;
}): WebflowPlaybookServiceRequestParams | null => {
  if (
    !playbookId ||
    !customerId ||
    !userEmail ||
    !pageText ||
    !selectedText ||
    !selectedTag
  ) {
    return null;
  }
  const baseBody = {
    customer_id: customerId,
    user_id: userEmail,
    page_text: pageText,
    selected_tag: selectedTag,
  };
  switch (playbookId) {
    case 'headline':
      return {
        ...baseBody,
        service_name: 'Tailored Headline',
        selected_text: selectedText,
      };
    case 'non_commital_cta':
      return {
        ...baseBody,
        service_name: 'Non-Committal CTA',
        selected_cta: selectedText,
      };
    case 'social_proof':
      return {
        ...baseBody,
        service_name: 'Social Proof Headline',
        selected_text: selectedText,
      };
    case 'plain_language':
      return {
        ...baseBody,
        service_name: 'Plain Language Headline',
        selected_text: selectedText,
      };
    case 'landing_page_2nd_person':
      return {
        ...baseBody,
        service_name: 'Landing Page 2nd Person',
        selected_text: selectedText,
      };
    case 'user_guided_text_change':
      if (!customPrompt) {
        return null;
      }
      return {
        ...baseBody,
        service_name: 'User Guided Text Change',
        selected_text: selectedText,
        user_guidance: customPrompt,
      };
    default:
      return null;
  }
};

interface SuggestionsResponse {
  data: {
    requestId: string;
    text: {id: string; text: string}[];
  };
  success: boolean;
}

const errorMessage =
  'Failed to generate suggestions. Please select another element.';

export function _useAiSuggestions(
  useDispatch: UseDispatch,
  useDesignerSelector: UseDesignerSelector,
  useTrackEvent: UseTrackEvent
) {
  return function useAiSuggestions({
    experienceId,
    userGuidance,
    variation,
    elementId,
  }: {
    experienceId: string | null;
    userGuidance: string | null;
    variation: OptimizationVariation | null;
    elementId: ExpressionElement['id'] | null;
  }) {
    const dispatch = useDispatch();
    const [suggestions, setSuggestions] =
      useState<SuggestionOptionsType | null>(null);
    const [error, setError] = useState<boolean>(false);

    const customPrompt = useDesignerSelector(selectCustomPrompt);
    const playbookId = useDesignerSelector(selectCurrentPlaybookId);
    const siteShortName = useDesignerSelector(selectSiteShortName);
    const customerId = useDesignerSelector(selectCustomerId);
    const notify = useNotification(dispatch);
    const trackEvent = useTrackEvent();
    const API_PROXY_PATH = getAPIProxyPath(siteShortName);
    const selectedElement = useDesignerSelector(selectSelectedElement);
    const selectedNativeIdPath = useDesignerSelector(
      selectSelectedNativeIdPath
    );
    const previousSelectedElement = usePreviousElement(selectedElement);
    const userEmail = useDesignerSelector(selectCurrentUserEmail);
    const pageText = useDesignerSelector(selectPageText);
    const isValidSelection = useDesignerSelector(
      selectIsValidSuggestionSelection
    );
    const isSelectedElementInAnotherExperience = useDesignerSelector(
      selectIsSelectedElementInAnotherExperience
    );
    const isValidSuggestionSelection =
      isValidSelection && !isSelectedElementInAnotherExperience;

    useEffect(() => {
      setSuggestions(null);
    }, [customPrompt]);

    const onError = useCallback(() => {
      setError(true);
      notify(errorMessage, 'error');
      trackEvent(
        // @ts-expect-error (TS2820) - TODO: Add event type to Segment tracking plan using https://github.com/webflow/segment-tracking-plan-management
        ANALYTICS_EVENTS.OPTIMIZATION_AI_SUGGESTION_GENERATION_FAILED,
        {
          playbookId: playbookId ?? '',
          elementType: selectedElement?.type.join(':'),
          optimizationId: experienceId,
          variationId: variation?.id,
        }
      );
    }, [
      experienceId,
      playbookId,
      notify,
      selectedElement?.type,
      trackEvent,
      variation?.id,
    ]);

    const query = useQuery<SuggestionsResponse>({
      queryKey: queryKeys.aiSuggestions({
        elementId,
        playbookId,
        customPrompt,
      }),
      retry: false,
      queryFn: async () => {
        if (!selectedElement || experienceId === TEMPORARY_EXPERIENCE_ID) {
          return Promise.reject();
        }
        if (!isValidSuggestionSelection) {
          // When the user has selected an invalid element,
          // track the event
          trackEvent(
            // @ts-expect-error (TS2820) - TODO: Add event type to Segment tracking plan using https://github.com/webflow/segment-tracking-plan-management
            ANALYTICS_EVENTS.OPTIMIZATION_AI_SUGGESTION_INVALID_ELEMENT_SELECTED,
            {
              playbookId: playbookId ?? '',
              elementType: selectedElement.type.join(':'),
              optimizationId: experienceId,
              variationId: variation?.id,
            }
          );
          return Promise.reject();
        }
        // When we change the selected element, clear the suggestions
        if (
          previousSelectedElement &&
          previousSelectedElement.id !== selectedElement.id
        ) {
          setError(false);
          setSuggestions(null);
        }

        const selectedText = getTextFromNativeIdPath(selectedNativeIdPath);
        const selectedTag = getTag(selectedElement);

        const body = getRequestBody({
          playbookId,
          customerId,
          userEmail,
          pageText,
          selectedText,
          selectedTag,
          customPrompt: (customPrompt || userGuidance) ?? null,
        });
        if (!body) {
          onError();
          return Promise.reject();
        }

        try {
          const response = await fetch(
            `${API_PROXY_PATH}/api/v2/content-studio/${customerId}/wf-playbook-generator`,
            {
              method: 'POST',
              body: JSON.stringify(body),
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          if (!response.ok) {
            onError();
            return Promise.reject();
          }

          const json = await response.json();
          if (!json.success) {
            onError();
            return Promise.reject();
          }
          return json;
        } catch (e) {
          onError();
          return Promise.reject();
        }
      },
      onSuccess: (data) => {
        const newSuggestions = data.data.text.map((suggestion) => ({
          suggestion: suggestion.text,
          isSelected: false,
          override: null,
          variationId: null,
          variationName: null,
          id: suggestion.id,
        }));
        onReceiveSuggestions(newSuggestions);
      },
    });

    const onReceiveSuggestions = useCallback(
      (receivedSuggestions: SuggestionOptionsType) => {
        setSuggestions((previousSuggestions) => {
          if (!previousSuggestions) {
            return take(receivedSuggestions, MAX_SUGGESTIONS);
          }
          let count = 0;
          const newSuggestions = previousSuggestions.map((suggestion) => {
            if (suggestion.isSelected) {
              return suggestion;
            }
            const newSuggestion = receivedSuggestions[count];
            if (newSuggestion) {
              count += 1;
            }
            return newSuggestion || suggestion;
          });

          // if we didn't have enough suggestions from a previous response
          // fill the new suggestions with newly received ones
          while (
            newSuggestions.length < MAX_SUGGESTIONS &&
            count < receivedSuggestions.length - 1
          ) {
            const newSuggestion = receivedSuggestions[count];
            if (newSuggestion) {
              count += 1;
              newSuggestions.push(newSuggestion);
            }
          }
          return newSuggestions;
        });
      },
      []
    );

    const onSelectSuggestion = useCallback((id: string) => {
      setSuggestions((previousSuggestions) => {
        if (!previousSuggestions) {
          return null;
        }
        return previousSuggestions.map((suggestion) => {
          if (suggestion.id === id) {
            return {...suggestion, isSelected: !suggestion.isSelected};
          }
          return suggestion;
        });
      });
    }, []);

    const onOverrideSuggestion = useCallback(
      (id: string, override: string | null) => {
        setSuggestions((previousSuggestions) => {
          if (!previousSuggestions) {
            return null;
          }
          return previousSuggestions.map((previousSuggestion) => {
            if (previousSuggestion.id !== id) {
              return previousSuggestion;
            }
            if (
              override === null ||
              override === previousSuggestion.suggestion
            ) {
              return {
                ...previousSuggestion,
                override: null,
              };
            }
            return {...previousSuggestion, override, isSelected: true};
          });
        });
      },
      []
    );

    const onClearSuggestions = useCallback(() => {
      setSuggestions(null);
    }, []);

    const onRegenerateSuggestions = useCallback(
      (shouldClearSuggestions?: boolean) => {
        setError(false);
        if (shouldClearSuggestions === true) {
          onClearSuggestions();
        }
        query.refetch();
      },
      [onClearSuggestions, query]
    );

    return {
      suggestions,
      onSelectSuggestion,
      onOverrideSuggestion,
      onRegenerateSuggestions,
      onClearSuggestions,
      isFetchingSuggestions: query.isFetching,
      isValidSuggestionSelection: isValidSuggestionSelection && !error,
      isError: error,
    };
  };
}
